import { IconBell } from '@developerz/ui';
import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import { notificationCopy } from '../lib/notification-copy';
import { refreshInboxOnPush } from '../lib/notification-push';
import { playStoredNotificationSound } from '../lib/notification-sound';
import { useNotificationInbox } from '../lib/notifications';
import { orgDeepLink } from '../lib/org';
import { useStreamTopics } from '../lib/stream-topics';
import { ago } from '../lib/time';
import { LoadMore } from './LoadMore';
import { Popover } from './Popover';
import { buttonClass } from './ui/Button';

// Topbar notifications bell (#809): the caller's recent notifications. The
// `notifications` SSE topic says when to RE-READ them — rows and badge count
// together (`lib/notification-push.ts`), never on a timer — and the panel lists
// the server's answer, never a stream row. A click on a row
// navigates its `deep_link`, scoped to the URL org the inbox was read under
// (`orgDeepLink`), and marks the event read. The inbox is one row per EVENT — the API collapses the
// per-channel ledger rows by dedupe key — so we render the notification itself,
// never a per-channel delivery status (`sent`/`suppressed`/`pending` are ledger
// detail, not user-facing). The badge is the API's account-wide `unread_count`
// (clearable via mark-read / mark-all-read), never the loaded-row count.
//
// Mounted only for a session with a PERSON (`NotificationBell` decides): the inbox
// and its stream topic are personal, and a user-less bearer is refused both.

export function NotificationInboxBell(props: {
  readonly token: string | null;
  /** The tone for an arrival (#5890); the stored choice by default. A test passes a counter. */
  readonly playSound?: (() => Promise<boolean>) | undefined;
}): JSX.Element {
  const token = props.token;
  const inbox = useNotificationInbox(token);
  // Registered on the shell's ONE connection, not a socket of the bell's own:
  // this component sits in `AppTopbar`, so it is on EVERY page and was the
  // reason every page held a stream before anything else opened one.
  const stream = useStreamTopics(['notifications'], token);
  // The stream decides WHEN; the inbox's REST read is WHAT (rows and count).
  refreshInboxOnPush({
    streamRows: () => stream.list('notifications'),
    status: stream.status,
    fetchedRows: inbox.paged.rows,
    // The mount read LANDED, empty included: an empty inbox's first arrival is
    // news (plan 101 slice 17 row 17-5).
    settled: inbox.settled,
    refresh: inbox.refresh,
    // On an ARRIVAL — a row this tab has never seen, past the watermark, still
    // unread (#5890) — play the stored tone. `playStoredNotificationSound`
    // reads the stored choice and is a no-op when it is `'off'`, the default.
    onArrival: () => {
      void (props.playSound ?? playStoredNotificationSound)();
    },
  });

  const items = inbox.paged.rows;
  const badge = (): string => (inbox.unread() > 9 ? '9+' : String(inbox.unread()));

  // The unread count is the one state the bell conveys — it rides the label so
  // it isn't sighted-only (the visual badge stays aria-hidden).
  const label = (): string =>
    inbox.unread() > 0
      ? tPlural('notifications.open_unread', inbox.unread(), { count: inbox.unread() })
      : t('notifications.open');

  // Panel mechanics are <Popover/>'s: outside click, Escape only when no dialog
  // (the ⌘K palette, say) sits above the panel, and focus back on the bell.
  return (
    <Popover
      class="notif-bell"
      label={label()}
      triggerClass={`${buttonClass({ variant: 'ghost', size: 'icon' })} notif-bell__btn`}
      panelClass="notif-panel card"
      trigger={
        <>
          <IconBell size="1rem" />
          <Show when={inbox.unread() > 0}>
            <span class="notif-bell__count" aria-hidden="true">
              {badge()}
            </span>
          </Show>
        </>
      }
    >
      {(close) => (
        <>
          <header class="notif-panel__head">
            {t('notifications.title')}
            <Show when={inbox.unread() > 0}>
              <button
                type="button"
                class={`${buttonClass({ variant: 'link' })} notif-panel__mark-all`}
                onClick={() => inbox.markAllRead()}
              >
                {t('notifications.mark_all_read')}
              </button>
            </Show>
          </header>
          <Show
            when={items().length > 0}
            fallback={<p class="notif-panel__empty">{t('notifications.empty')}</p>}
          >
            <ul class="notif-panel__list">
              <For each={items()}>
                {(n) => (
                  <li class="notif-item">
                    <A
                      class="notif-item__link"
                      href={orgDeepLink(n.deep_link)}
                      onClick={() => {
                        inbox.markRead(n);
                        close();
                      }}
                    >
                      {/* Spelled from `kind` + `payload` in the reader's language when the
                          row carries every argument (`lib/notification-copy.ts`, #3769);
                          the server's English otherwise. */}
                      <Show
                        when={notificationCopy(n, t)}
                        fallback={
                          <>
                            <span class="notif-item__title">{n.title}</span>
                            <span class="notif-item__body">{n.body}</span>
                          </>
                        }
                      >
                        {(copy) => (
                          <>
                            <span class="notif-item__title">{copy().title}</span>
                            <span class="notif-item__body">{copy().body}</span>
                          </>
                        )}
                      </Show>
                      <span class="notif-item__meta">
                        <span class="notif-item__time">{ago(n.created_at)}</span>
                      </span>
                    </A>
                  </li>
                )}
              </For>
            </ul>
            <LoadMore list={inbox.paged} />
          </Show>
        </>
      )}
    </Popover>
  );
}
